import { useState, type ReactNode } from 'react';
import { Action, Subject } from '@erp/authz';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardTextIcon,
  CubeIcon,
  PlusIcon,
  StackIcon,
} from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  QueryIRSchema,
  type WorkbookCatalogEntry,
  type WorkbookDefinition,
  type WorkbookSource,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCan } from '@/hooks/useCan';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { pastedTableSource } from './pastedTable';
import { AddDataDescription, FileDropArea } from './WorkbookAddDataDrop';
import { CatalogGapsHint } from './WorkbookInputs';
import { SchemaGroupRow } from './WorkbookSchemaGroupRows';
import { AddDataItemPreview } from './WorkbookAddDataPreview';
import { WorkbookItemIcon } from './workbookItemIcon';
import {
  CATALOG_SEARCH_THRESHOLD,
  catalogGaps,
  catalogSource,
  catalogView,
  type WorkbookCatalogKind,
} from './workbookCatalog';
import {
  addDataSections,
  entryTitle,
  groupPreviewSource,
  matchingObjects,
  previewSource,
  resolveAddDataSelection,
  type AddDataItem,
  type AddDataSection,
} from './workbookAddDataItems';
import { groupSchemas } from './workbookSchemaGroup';
import { useGroupChoices } from './useGroupChoices';
import { isDuplicateSource } from './workbookSourceIdentity';
import { AddedButton } from './WorkbookAddedButton';

/**
 * What Add data reads of the Workbook: its catalog and Definition, and its id
 * to preview data with — null before the Workbook exists, which shows no preview.
 */
export interface AddDataContext {
  workbookId: string | null;
  definition: WorkbookDefinition;
  catalog: {
    data: readonly WorkbookCatalogEntry[] | undefined;
    error: Error | null;
  };
}

function SectionTitle({ kind }: { kind: AddDataSection['kind'] }) {
  switch (kind) {
    case 'data-source':
      return <T context="workbook catalog group">Data sources</T>;
    case 'mapping':
      return <T context="workbook catalog group">Data mappings</T>;
    case 'stateful-table':
      return <T context="workbook catalog group">Stateful tables</T>;
    case 'data-asset':
      return <T context="workbook catalog group">Data assets</T>;
    case 'forecast-snapshots':
      return <T context="workbook catalog group">Forecasts</T>;
    case 'entity':
      return <T context="workbook catalog group">Gently entities</T>;
    case 'other':
      return (
        <T context="workbook add data group: ways not in the catalog">Other</T>
      );
  }
}

/** A row's title and the line under it. */
function ItemLabel({ item }: { item: AddDataItem }) {
  switch (item.type) {
    case 'entry': {
      const { title, detail } = entryTitle(item.entry);
      return (
        <>
          <span className="block truncate text-sm text-foreground">
            {title}
          </span>
          {detail && (
            <span className="block truncate font-mono text-[11px] text-muted-foreground">
              {detail}
            </span>
          )}
        </>
      );
    }
    case 'group':
      return (
        <>
          <span className="block truncate text-sm text-foreground">
            {item.group.schemaName}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            <T context="workbook add data list: how many data sources share a schema">
              <Num>{item.group.linkCount}</Num>{' '}
              <Plural
                n={item.group.linkCount}
                one="data source"
                other="data sources"
              />{' '}
              share these columns
            </T>
          </span>
        </>
      );
    case 'object':
      return (
        <span className="block truncate text-sm text-foreground">
          {item.label}
        </span>
      );
    case 'paste':
      return (
        <span className="block truncate text-sm text-foreground">
          <T context="workbook paste input">Paste a small table</T>
        </span>
      );
  }
}

function ItemIcon({ item }: { item: AddDataItem }) {
  const className = 'mt-0.5 size-4 shrink-0 text-muted-foreground';
  switch (item.type) {
    case 'group':
      return <StackIcon className={className} aria-hidden />;
    case 'object':
      return <CubeIcon className={className} aria-hidden />;
    case 'paste':
      return <ClipboardTextIcon className={className} aria-hidden />;
    case 'entry':
      // An Entity sits among the Gently objects, as one of Gently's records.
      if (item.entry.kind === 'entity')
        return <CubeIcon className={className} aria-hidden />;
      return (
        <WorkbookItemIcon
          kind={item.entry.kind === 'mapping' ? 'mapping' : 'data'}
          aria-hidden
          className={className}
        />
      );
  }
}

function AddDataRow({
  item,
  selected,
  onSelect,
  onAdd,
}: {
  item: AddDataItem;
  selected: boolean;
  onSelect: () => void;
  /** Adds the item straight away; absent for items that need choices first. */
  onAdd?: () => void;
}) {
  const gt = useGT();
  const title = item.type === 'entry' ? entryTitle(item.entry).title : null;
  return (
    <li
      className={cn(
        'group/row flex items-center gap-1 pr-1.5 transition-colors',
        selected ? 'bg-muted' : 'hover:bg-muted/60',
      )}
    >
      <button
        type="button"
        data-add-data-row
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-start gap-2 py-1.5 pl-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <ItemIcon item={item} />
        <span className="min-w-0 flex-1">
          <ItemLabel item={item} />
        </span>
      </button>
      {onAdd && title && (
        <Button
          size="icon"
          variant="ghost"
          className="size-7 shrink-0 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 group-has-[[aria-current]]/row:opacity-100"
          aria-label={gt('Add {name}', {
            name: title,
            context: 'workbook add data list: add this entry straight away',
          })}
          onClick={onAdd}
        >
          <PlusIcon />
        </Button>
      )}
    </li>
  );
}

/** Arrow keys move between rows, selecting as they go. */
function handleListKeyDown(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const rows = Array.from(
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-add-data-row]'),
  );
  const index = rows.findIndex((row) => row === document.activeElement);
  if (index === -1) return;
  e.preventDefault();
  const next = rows[index + (e.key === 'ArrowDown' ? 1 : -1)];
  if (next) {
    next.focus();
    next.click();
  }
}

function KindLabel({ kind }: { kind: WorkbookCatalogKind }) {
  switch (kind) {
    case 'data-source':
      return (
        <T context="workbook add data detail: kind of entry">Data source</T>
      );
    case 'mapping':
      return (
        <T context="workbook add data detail: kind of entry">Data mapping</T>
      );
    case 'stateful-table':
      return (
        <T context="workbook add data detail: kind of entry">Stateful table</T>
      );
    case 'data-asset':
      return (
        <T context="workbook add data detail: kind of entry">Data asset</T>
      );
    case 'forecast-snapshots':
      return (
        <T context="workbook add data detail: kind of entry">
          Forecast snapshots
        </T>
      );
    case 'entity':
      return <T context="workbook add data detail: kind of entry">Entity</T>;
  }
}

/** The columns a catalog entry brings, each with the type the schema declares. */
function EntryColumns({
  columns,
}: {
  columns: NonNullable<WorkbookCatalogEntry['columns']>;
}) {
  if (columns.length === 0) return null;
  return (
    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold text-foreground">
        <T context="workbook add data detail: the entry's columns">
          Columns{' '}
          <span className="font-normal text-muted-foreground tabular-nums">
            <Num>{columns.length}</Num>
          </span>
        </T>
      </h3>
      <ul className="flex flex-wrap gap-1">
        {columns.map((column) => (
          <li
            key={column.name}
            title={column.description}
            className="rounded-md bg-muted px-1.5 py-0.5 text-xs"
          >
            {column.label || column.name}
            <span className="ml-1 text-muted-foreground">{column.type}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DetailHeader({
  title,
  meta,
  description,
  action,
}: {
  title: ReactNode;
  meta?: ReactNode;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-0.5">
        <h3 className="truncate text-base font-semibold text-foreground">
          {title}
        </h3>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
        {description && (
          <p className="max-w-prose text-sm whitespace-pre-line text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {action}
    </header>
  );
}

function EntryDetail({
  editor,
  entry,
  onAdd,
}: {
  editor: AddDataContext;
  entry: WorkbookCatalogEntry;
  onAdd: () => void;
}) {
  const { title, detail } = entryTitle(entry);
  const source = previewSource({
    key: `entry:${entry.id}`,
    type: 'entry',
    entry,
  });
  return (
    <div className="flex min-h-full flex-col gap-4">
      <DetailHeader
        title={title}
        meta={
          <>
            <KindLabel kind={entry.kind} />
            {detail && (
              <>
                {' · '}
                <span className="font-mono">{detail}</span>
              </>
            )}
          </>
        }
        description={entry.description}
        action={
          <Button size="sm" className="shrink-0" onClick={onAdd}>
            <PlusIcon />
            <T context="workbook add data detail: add the selected entry">
              Add to Workbook
            </T>
          </Button>
        }
      />
      <EntryColumns columns={entry.columns ?? []} />
      {source && (
        <AddDataItemPreview key={source.id} editor={editor} source={source} />
      )}
    </div>
  );
}

/** How long the label column must stop changing before the preview reads it. */
export const PREVIEW_LABEL_DEBOUNCE_MS = 500;

function GroupDetail({
  editor,
  item,
  catalog,
  query,
  choices,
  onSource,
}: {
  editor: AddDataContext;
  item: Extract<AddDataItem, { type: 'group' }>;
  catalog: readonly WorkbookCatalogEntry[];
  query: string;
  choices: ReturnType<typeof useGroupChoices>;
  onSource: (source: WorkbookSource) => boolean;
}) {
  const { group } = item;
  const choice = choices.choiceOf(group.schemaId);
  // A combined preview reads every member, so it follows the label column once
  // typing pauses rather than on each keystroke.
  const labelColumn = useDebounce(
    choice.labelColumn,
    PREVIEW_LABEL_DEBOUNCE_MS,
  );
  const source = groupPreviewSource(group, { ...choice, labelColumn });
  return (
    <div className="flex min-h-full flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        <T context="workbook add data detail: a schema several data sources share">
          <Num>{group.linkCount}</Num>{' '}
          <Plural n={group.linkCount} one="data source" other="data sources" />{' '}
          with the same columns. Combine them into one table, or add them one by
          one.
        </T>
      </p>
      <SchemaGroupRow
        group={group}
        choice={choice}
        onChoice={(patch) => choices.choose(group.schemaId, patch)}
        catalog={catalog}
        query={query}
        definition={editor.definition}
        onSource={onSource}
      />
      {source && (
        <AddDataItemPreview key={source.id} editor={editor} source={source} />
      )}
    </div>
  );
}

/** The Source reading a Gently object's chosen fields; none until a field is chosen. */
function objectSource(
  base: string,
  label: string,
  fields: readonly string[],
): WorkbookSource | null {
  if (!base || !fields.length) return null;
  return {
    id: crypto.randomUUID(),
    kind: 'object',
    folder: '',
    name: label,
    query: QueryIRSchema.parse({ base, select: fields }),
  };
}

function ObjectForm({
  base,
  label,
  fields,
  setFields,
  sources,
  onSource,
}: {
  /** The Gently object: a query builder subject area's key, and its name. */
  base: string;
  label: string;
  fields: readonly string[];
  setFields: (fields: string[]) => void;
  /** The Workbook's Sources, to tell when these fields are in it already. */
  sources: readonly WorkbookSource[];
  onSource: (source: WorkbookSource) => void;
}) {
  const chosen = new Set(fields);
  const source = objectSource(base, label, fields);
  const added = !!source && isDuplicateSource(sources, source);
  const catalog = useQuery(
    orpc.procurement.queryBuilder.catalog.queryOptions({ input: {} }),
  );
  return (
    <div className="space-y-3">
      <DetailHeader
        title={label}
        meta={
          <T context="workbook add data detail: what a Gently object is">
            Read records Gently keeps, with the fields you choose.
          </T>
        }
      />
      {catalog.error && (
        <p role="alert" className="text-destructive">
          {catalog.error.message}
        </p>
      )}
      <div className="grid max-h-80 grid-cols-2 gap-1 overflow-auto @3xl:grid-cols-3">
        {catalog.data?.fields.flatMap((field) =>
          field.subjectArea === base
            ? [
                <label
                  key={field.key}
                  className="flex items-center gap-2 text-xs"
                >
                  <Checkbox
                    checked={chosen.has(field.key)}
                    onCheckedChange={(checked) =>
                      setFields(
                        checked
                          ? [...fields, field.key]
                          : fields.filter((item) => item !== field.key),
                      )
                    }
                  />
                  {field.label}
                </label>,
              ]
            : [],
        )}
      </div>
      {added ? (
        <AddedButton />
      ) : (
        <Button
          size="sm"
          disabled={!source}
          onClick={() => source && onSource(source)}
        >
          <PlusIcon />
          <T context="workbook add object input">Add object</T>
        </Button>
      )}
    </div>
  );
}

function PasteForm({
  definition,
  pasted,
  setPasted,
  onSource,
}: {
  definition: WorkbookDefinition;
  pasted: string;
  setPasted: (pasted: string) => void;
  onSource: (source: WorkbookSource) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-3">
      <DetailHeader
        title={<T context="workbook paste input">Paste a small table</T>}
        meta={
          <T context="workbook paste guidance">
            Copy cells from a spreadsheet, including one header row.
          </T>
        }
      />
      <Textarea
        aria-label={gt('Table cells', { context: 'workbook pasted table' })}
        value={pasted}
        onChange={(event) => setPasted(event.target.value)}
        rows={8}
        className="font-mono text-xs"
      />
      <Button
        size="sm"
        disabled={!pasted.trim()}
        onClick={() => {
          const source = pastedTableSource(
            pasted,
            definition,
            gt('Pasted table', { context: 'workbook inline input name' }),
          );
          if (source) onSource(source);
        }}
      >
        <PlusIcon />
        <T context="workbook add pasted input">Add table</T>
      </Button>
    </div>
  );
}

/**
 * The schemas Data Sources share, each with its complete membership, from their
 * own uncapped list rather than the capped catalog; without Data Source read nothing is requested.
 */
function useSchemaGroups(canReadSources: boolean) {
  const groupable = useQuery({
    ...orpc.workbooks.groupableSchemas.queryOptions({ input: {} }),
    enabled: canReadSources,
  });
  return canReadSources ? groupSchemas(groupable.data ?? []) : [];
}

/** Whether the user may Read each kind of catalog entry. */
function useCatalogReadable(): (kind: WorkbookCatalogKind) => boolean {
  const [canReadSources] = useCan(Action.Read, Subject.DataSource);
  const [canReadMappings] = useCan(Action.Read, Subject.DataMapping);
  const [canReadAssets] = useCan(Action.Read, Subject.DataAsset);
  const [canReadStatefulTables] = useCan(Action.Read, Subject.Workflow);
  const [canReadForecasts] = useCan(Action.Read, Subject.Inventory);
  const canRead: Record<WorkbookCatalogKind, boolean> = {
    'forecast-snapshots': canReadForecasts,
    'data-source': canReadSources,
    mapping: canReadMappings,
    'stateful-table': canReadStatefulTables,
    'data-asset': canReadAssets,
    // Each Entity needs its own Read, which the server applied to the catalog.
    entity: true,
  };
  return (kind) => canRead[kind];
}

function SearchBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="border-b p-2">
      <Input
        type="search"
        className="h-8"
        aria-label={gt('Search data', { context: 'workbook catalog search' })}
        placeholder={gt('Search data', { context: 'workbook catalog search' })}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** The list's sections, each a heading with its count, then its rows. */
function AddDataList({
  sections,
  selectedKey,
  onSelect,
  onAddEntry,
  footer,
}: {
  sections: readonly AddDataSection[];
  selectedKey: string | undefined;
  onSelect: (key: string) => void;
  onAddEntry: (entry: WorkbookCatalogEntry) => void;
  footer: ReactNode;
}) {
  const gt = useGT();
  return (
    <nav
      aria-label={gt('Data to add', {
        context: 'aria-label for the workbook add data list',
      })}
      className="max-h-80 min-h-0 flex-1 overflow-y-auto @3xl:max-h-none"
      onKeyDown={handleListKeyDown}
    >
      {sections.map((section) => (
        <section key={section.kind} className="pb-1">
          <h3 className="sticky top-0 z-10 flex items-center gap-1.5 bg-background px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
            <SectionTitle kind={section.kind} />
            {section.kind !== 'other' && (
              <span className="tabular-nums">
                <Num>{section.items.length}</Num>
              </span>
            )}
          </h3>
          <ul>
            {section.items.map((item) => (
              <AddDataRow
                key={item.key}
                item={item}
                selected={item.key === selectedKey}
                onSelect={() => onSelect(item.key)}
                onAdd={
                  item.type === 'entry'
                    ? () => onAddEntry(item.entry)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      ))}
      {footer}
    </nav>
  );
}

/** What the right pane shows for the selected item. */
function AddDataDetail({
  editor,
  selected,
  query,
  choices,
  drafts,
  onSource,
}: {
  editor: AddDataContext;
  selected: AddDataItem | null;
  query: string;
  choices: ReturnType<typeof useGroupChoices>;
  drafts: ReturnType<typeof useAddDataDrafts>;
  onSource: (source: WorkbookSource) => boolean;
}) {
  switch (selected?.type) {
    case 'entry':
      return (
        <EntryDetail
          key={selected.key}
          editor={editor}
          entry={selected.entry}
          onAdd={() => onSource(catalogSource(selected.entry))}
        />
      );
    case 'group':
      return (
        <GroupDetail
          key={selected.key}
          editor={editor}
          item={selected}
          catalog={editor.catalog.data ?? []}
          query={query}
          choices={choices}
          onSource={onSource}
        />
      );
    case 'object':
      return (
        <ObjectForm
          key={selected.key}
          base={selected.base}
          label={selected.label}
          fields={drafts.objectFields[selected.base] ?? []}
          setFields={(fields) => drafts.setObjectFields(selected.base, fields)}
          sources={editor.definition.sources}
          onSource={onSource}
        />
      );
    case 'paste':
      return (
        <PasteForm
          definition={editor.definition}
          pasted={drafts.pasted}
          setPasted={drafts.setPasted}
          onSource={onSource}
        />
      );
    default:
      return null;
  }
}

/**
 * What the user has typed or chosen in the forms, kept above the detail pane
 * so selecting another item and coming back doesn't lose it.
 */
function useAddDataDrafts() {
  const [pasted, setPasted] = useState('');
  // The fields chosen for each Gently object, by its key.
  const [objectFields, setAllObjectFields] = useState<
    Readonly<Record<string, string[]>>
  >({});
  return {
    pasted,
    setPasted,
    objectFields,
    setObjectFields: (base: string, fields: string[]) =>
      setAllObjectFields((all) => ({ ...all, [base]: fields })),
  };
}

/** The Gently objects (query builder subject areas); none until read, or when they can't be. */
function useGentlyObjects(): readonly { key: string; label: string }[] {
  const catalog = useQuery(
    orpc.procurement.queryBuilder.catalog.queryOptions({ input: {} }),
  );
  return catalog.data?.subjectAreas ?? [];
}

/** The list's model: what to show for a search, and whether to offer one. */
function useAddDataList(editor: AddDataContext, query: string) {
  const canRead = useCatalogReadable();
  const entries = editor.catalog.data ?? [];
  const schemaGroups = useSchemaGroups(canRead('data-source'));
  const { available, sections, shownGroups } = catalogView(
    entries,
    editor.definition.sources,
    schemaGroups,
    query,
    canRead,
    new Map(schemaGroups.map((group) => [group.schemaId, group.members])),
  );
  const objects = useGentlyObjects();
  const shownObjects = matchingObjects(objects, query);
  const offered = available.length + schemaGroups.length + objects.length;
  return {
    sections: addDataSections(sections, shownGroups, shownObjects),
    searchable: offered > CATALOG_SEARCH_THRESHOLD,
    noMatch:
      query.trim() !== '' &&
      offered > 0 &&
      sections.length === 0 &&
      shownObjects.length === 0,
    gaps: catalogGaps(entries, canRead),
  };
}

/**
 * Add data: everything a Workbook can read, listed on the left by kind, with
 * the selected one described on the right — its columns and first rows, and
 * how to add it — so data is seen before it is added. Each catalog row also
 * adds in one click.
 */
export function AddInputPanel({
  editor,
  onSource,
  embedded = false,
  uploadFiles,
}: {
  editor: AddDataContext;
  onSource: (source: WorkbookSource) => boolean;
  /** Inside a step that titles it already: no heading, and a fixed height. */
  embedded?: boolean;
  /** Uploads dropped or chosen spreadsheet files; absent, none are taken. */
  uploadFiles?: (files: readonly File[]) => Promise<void>;
}) {
  const gt = useGT();
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const choices = useGroupChoices();
  const drafts = useAddDataDrafts();
  const list = useAddDataList(editor, query);
  const selected = resolveAddDataSelection(list.sections, selectedKey);
  const onFiles = uploadFiles && ((files: File[]) => void uploadFiles(files));
  // Data already in the Workbook isn't added again.
  const addSource = (source: WorkbookSource) =>
    !isDuplicateSource(editor.definition.sources, source) && onSource(source);

  return (
    <FileDropArea onFiles={onFiles}>
      <div className="flex h-full flex-col gap-3">
        {!embedded && (
          <div className="space-y-0.5">
            <h2 className="font-medium">
              <T context="workbook add input title">Add data</T>
            </h2>
            <AddDataDescription onFiles={onFiles} />
          </div>
        )}
        {editor.catalog.error && (
          <p role="alert" className="text-destructive">
            {editor.catalog.error.message}
          </p>
        )}
        <div className="@container min-h-0 flex-1">
          <div
            className={cn(
              'grid overflow-hidden rounded-lg border @3xl:grid-cols-[minmax(16rem,20rem)_1fr]',
              embedded
                ? '@3xl:h-[26rem]'
                : '@3xl:h-[calc(100dvh-14rem)] @3xl:min-h-[28rem]',
            )}
          >
            <aside className="flex min-h-0 flex-col border-b @3xl:border-r @3xl:border-b-0">
              {list.searchable && (
                <SearchBox value={query} onChange={setQuery} />
              )}
              {list.noMatch && (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  <T context="workbook catalog no match">
                    No data matches the search.
                  </T>
                </p>
              )}
              <AddDataList
                sections={list.sections}
                selectedKey={selected?.key}
                onSelect={setSelectedKey}
                onAddEntry={(entry) => addSource(catalogSource(entry))}
                footer={
                  !query.trim() && (
                    <div className="px-3 py-2">
                      <CatalogGapsHint gaps={list.gaps} />
                    </div>
                  )
                }
              />
            </aside>
            <section
              aria-label={gt('Selected data', {
                context: 'aria-label for the workbook add data detail pane',
              })}
              className="min-h-0 overflow-y-auto p-3"
            >
              <AddDataDetail
                editor={editor}
                selected={selected}
                query={query}
                choices={choices}
                drafts={drafts}
                onSource={addSource}
              />
            </section>
          </div>
        </div>
      </div>
    </FileDropArea>
  );
}
